import { Typography } from "antd";
import { FormattedMessage } from "react-intl";
import type { EntityGroupScope, LeagueScope, PermissionScopeResources } from "~types/permissionScope";
import { PermissionScopeTypes } from "../../../../enums/permissionScopeTypes";
import type { ScopeOption } from "../types/scopeOption";

const extractScopeData = (
  leagueTree: LeagueScope[],
  entityGroupTree: EntityGroupScope[],
  resources: PermissionScopeResources,
  scopeType: string,
): ScopeOption[] => {
  const {
    leagues: leaguesData = {},
    competitions: competitionsData = {},
    seasons: seasonsData = {},
    entityGroups: entityGroupsData = {},
    entities: entitiesData = {},
  } = resources;

  if (scopeType === PermissionScopeTypes.League) {
    return leagueTree.reduce<ScopeOption[]>((acc, currentValue) => {
      if (!currentValue.leagueId) {
        return acc;
      }

      const league = leaguesData[currentValue.leagueId];

      acc.push({
        value: currentValue.leagueId,
        label: (
          <Typography.Title className="permissions-scope-view-item-label" level={4}>
            {league?.nameLocal}
          </Typography.Title>
        ),
        data: [{ label: <FormattedMessage id="regionType" />, value: league?.regionType || "" }],
      });

      return acc;
    }, []);
  }

  if (scopeType === PermissionScopeTypes.Competition) {
    return leagueTree.reduce<ScopeOption[]>((acc, currentValue) => {
      const leagueCompetitions = currentValue.competitions.map(({ competitionId }) => {
        const competition = competitionsData[competitionId];
        return {
          value: competitionId,
          label: (
            <div>
              <Typography.Title className="permissions-scope-view-item-label" level={4}>
                {competition?.nameLocal}
              </Typography.Title>
              {competition?.leagueId && (
                <Typography.Text className="permissions-scope-view-item-label-subtitle">
                  <FormattedMessage id="league" />
                  {":  "}
                  {leaguesData[competition.leagueId]?.nameLocal}
                </Typography.Text>
              )}
            </div>
          ),
          data: [{ label: <FormattedMessage id="ageGroup" />, value: competition?.ageGroup || "" }],
        };
      });

      acc.push(...leagueCompetitions);

      return acc;
    }, []);
  }

  if (scopeType === PermissionScopeTypes.Season) {
    return leagueTree.reduce<ScopeOption[]>((acc, currentValue) => {
      const leagueSeasons: ScopeOption[] = [];
      const league = currentValue.leagueId ? leaguesData[currentValue.leagueId] : undefined;

      currentValue.competitions.forEach(({ competitionId, seasonIds }) => {
        seasonIds.forEach((seasonId) => {
          const season = seasonsData[seasonId];

          leagueSeasons.push({
            value: seasonId,
            label: (
              <div>
                <Typography.Text className="permissions-scope-view-item-label">{season?.nameLocal}</Typography.Text>
                <Typography.Text className="permissions-scope-view-item-label-subtitle">
                  <FormattedMessage id="competition" />
                  {":  "}
                  {competitionsData[competitionId]?.nameLocal}
                </Typography.Text>
                {league && (
                  <Typography.Text className="permissions-scope-view-item-label-subtitle" style={{ display: "block", marginTop: "-4px" }}>
                    <FormattedMessage id="league" />
                    {":  "}
                    {league.nameLocal}
                  </Typography.Text>
                )}
              </div>
            ),
            data: [
              { label: <FormattedMessage id="gender" />, value: season?.gender },
              { label: <FormattedMessage id="ageGroup" />, value: season?.ageGroup },
              { label: <FormattedMessage id="discipline" />, value: season?.discipline },
              { label: <FormattedMessage id="status" />, value: season?.status },
            ],
          });
        });
      });

      acc.push(...leagueSeasons);

      return acc;
    }, []);
  }

  if (scopeType === PermissionScopeTypes.EntityGroup) {
    return entityGroupTree.reduce<ScopeOption[]>((acc, currentValue) => {
      if (!currentValue.entityGroupId) {
        return acc;
      }
      const entityGroup = entityGroupsData[currentValue.entityGroupId];

      acc.push({
        value: currentValue.entityGroupId,
        label: (
          <Typography.Title className="permissions-scope-view-item-label" level={4}>
            {entityGroup?.nameFullLocal}
          </Typography.Title>
        ),
        data: [{ label: <FormattedMessage id="status" />, value: entityGroup?.status }],
      });

      return acc;
    }, []);
  }

  if (scopeType === PermissionScopeTypes.Entity) {
    return entityGroupTree.reduce<ScopeOption[]>((acc, currentValue) => {
      const entityGroup = currentValue.entityGroupId ? entityGroupsData[currentValue.entityGroupId] : undefined;
      const entityGroupEntities = currentValue.entityIds.map((entityId) => {
        const entity = entitiesData[entityId];

        return {
          value: entityId,
          label: (
            <div>
              <Typography.Title className="permissions-scope-view-item-label" level={4}>
                {entity?.nameFullLocal}
              </Typography.Title>
              <Typography.Text className="permissions-scope-view-item-label-subtitle">
                <FormattedMessage id="team.id" />
                {":  "}
                {entityId}
              </Typography.Text>
              {entityGroup && (
                <Typography.Text className="permissions-scope-view-item-label-subtitle" style={{ display: "block", marginTop: "-4px" }}>
                  <FormattedMessage id="club" />
                  {":  "}
                  {entityGroup.nameFullLocal}
                </Typography.Text>
              )}
            </div>
          ),
          data: [
            { label: <FormattedMessage id="gender" />, value: entity?.gender },
            { label: <FormattedMessage id="ageGroup" />, value: entity?.ageGroup },
            { label: <FormattedMessage id="discipline" />, value: entity?.discipline },
            { label: <FormattedMessage id="status" />, value: entity?.status },
          ],
        };
      });
      acc.push(...entityGroupEntities);
      return acc;
    }, []);
  }

  return [];
};

export default extractScopeData;
